import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../routes';

/* ─── interface-design: Token Architecture ──────────────────────────────────
   Palette derivada del dominio hipódromo:
   - Fondo de cards: bg-white / dark:bg-gray-800 (igual que toda la app)
   - Rareza 5: dorado con pulso  — sd-rarity-5-glow se inyecta desde SkillDetail.js
   - Tipo de skill: franja izquierda de color como signature element del proyecto
   - Evolution links: superficies elevadas con hover que sube un nivel
*/

const SKILL_TYPE_COLOR_MAP = {
    'Unique Skills': 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-700',
    'Inherited Skills': 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600',
    'Green Skills': 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-200 dark:border-green-700',
    'Blue Skills': 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-700',
    'Red Skills': 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700',
    'Orange Skills': 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-700',
    'Purple Skills': 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/30 dark:text-purple-200 dark:border-purple-700',
    'Gold skills': 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700',
    'Scenario Gold Skill': 'bg-amber-200 text-amber-900 border-amber-400 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-600',
    'Evolution Skills': 'bg-pink-100 text-pink-800 border-pink-300 dark:bg-pink-900/30 dark:text-pink-200 dark:border-pink-700',
};

const SKILL_TYPE_BORDER = {
    'Unique Skills': 'border-l-yellow-400',
    'Inherited Skills': 'border-l-gray-300 dark:border-l-gray-600',
    'Green Skills': 'border-l-green-500',
    'Blue Skills': 'border-l-blue-500',
    'Red Skills': 'border-l-red-500',
    'Orange Skills': 'border-l-orange-400',
    'Purple Skills': 'border-l-purple-500',
    'Gold skills': 'border-l-amber-400',
    'Scenario Gold Skill': 'border-l-amber-600',
    'Evolution Skills': 'border-l-pink-500',
};

/* interface-design: Subtle Layering — icon ring expresa rareza sin texto */
const RARITY_RING = {
    1: 'ring-2 ring-gray-300 dark:ring-gray-600',
    2: 'ring-2 ring-green-400',
    3: 'ring-2 ring-blue-400',
    4: 'ring-2 ring-purple-400',
    5: 'ring-2 ring-yellow-400 sd-rarity-5-glow',
};
const RARITY_BORDER = {
    1: 'border-gray-300', 2: 'border-green-400',
    3: 'border-blue-400', 4: 'border-purple-400', 5: 'border-yellow-400'
};
const RARITY_BG = {
    1: 'bg-gray-50', 2: 'bg-green-50',
    3: 'bg-blue-50', 4: 'bg-purple-50', 5: 'bg-yellow-50'
};
const RARITY_STAR = {
    1: 'text-gray-400', 2: 'text-green-500',
    3: 'text-blue-500', 4: 'text-purple-500', 5: 'text-yellow-500'
};

/* ─── Sub-components ─────────────────────────────────────────────────────── */

/* interface-design: el icono con ring de rareza  es el signature element */
const SkillIconHero = ({ iconId, rarity }) => (
    <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
        <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border-2
            ring-offset-2 dark:ring-offset-gray-800
            ${RARITY_BORDER[rarity] || 'border-gray-300'}
            ${RARITY_BG[rarity] || 'bg-gray-50'}
            ${RARITY_RING[rarity] || 'ring-2 ring-gray-300'}
            flex items-center justify-center`}>
            <img
                src={`/images/Icon/Skill_Icons/icon_skill_${iconId}.png`}
                alt={`Skill icon ${iconId}`}
                className="w-full h-full object-cover"
                loading="eager"
                onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span style="font-size:1.5rem;font-weight:bold;color:#6b7280">${rarity ?? '?'}</span>`;
                }}
            />
        </div>
        {/* Estrellas — data-visualization: Secondary Action: labels siguen al elemento */}
        {rarity && (
            <div className={`flex gap-px text-[13px] leading-none ${RARITY_STAR[rarity] || 'text-gray-400'}`}>
                {[1, 2, 3, 4, 5].map(s => <span key={s}>{s <= rarity ? '★' : '☆'}</span>)}
            </div>
        )}
    </div>
);

/* interface-design: Evolution link como superficie elevada (un nivel sobre el card base) */
const EvolutionLink = ({ skill, label }) => {
    const navigate = useNavigate();
    if (!skill) return null;

    return (
        <button
            onClick={() => navigate(`${ROUTES.SKILL_DETAIL}/${skill.Id_skill}`)}
            className="group flex items-center gap-3 px-4 py-3 rounded-xl
                bg-gray-50 dark:bg-gray-700/60
                border border-gray-200 dark:border-gray-600
                hover:bg-gray-100 dark:hover:bg-gray-700
                hover:border-gray-300 dark:hover:border-gray-500
                hover:shadow-sm
                transition-all duration-200 text-left w-full sm:w-auto"
        >
            {/* Mini-icono */}
            <div className={`w-10 h-10 rounded-xl overflow-hidden border flex-shrink-0
                bg-gray-100 dark:bg-gray-800
                ${RARITY_BORDER[skill.rarity] || 'border-gray-300'}`}>
                <img
                    src={`/images/Icon/Skill_Icons/icon_skill_${skill.icon_id}.png`}
                    alt={skill.Skill_name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            </div>
            <div className="flex flex-col min-w-0">
                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500 mb-0.5">
                    {label}
                </span>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200
                    group-hover:text-blue-600 dark:group-hover:text-blue-400
                    transition-colors truncate max-w-[160px]"
                    style={{ fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.01em' }}>
                    {skill.Skill_name || `Skill #${skill.Id_skill}`}
                </span>
            </div>
            <svg className="w-3.5 h-3.5 text-gray-350 dark:text-gray-500 ml-auto flex-shrink-0
                group-hover:text-blue-500 dark:group-hover:text-blue-400
                group-hover:translate-x-0.5 transition-all duration-200"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
        </button>
    );
};

/* interface-design: data pill — información técnica en monospace es parte del domain */
const StatPill = ({ label, value, accent }) => {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="flex flex-col items-start gap-0.5">
            <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                {label}
            </span>
            <span className={`text-base font-bold tabular-nums leading-none ${accent}`}
                style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {value}
            </span>
        </div>
    );
};

/* ─── Main Header ─────────────────────────────────────────────────────────── */

/**
 * SkillDetailHeader
 * Props: skill, baseSkill | null, upgradeSkill | null
 *
 * interface-design decisions:
 *  - border-l-4 coloreado por tipo: signature element del proyecto, consistente con SkillTable
 *  - Tipografía Rajdhani para nombre: técnica/deportiva, no genérica (Inter rechazada)
 *  - JetBrains Mono para valores: enfatiza densidad analítica del dominio
 *  - Gradiente muy sutil en el hero para separar del resto sin romper la cohesión
 */
const SkillDetailHeader = ({ skill, baseSkill, upgradeSkill }) => {
    const borderClass = SKILL_TYPE_BORDER[skill.color] || 'border-l-gray-300';
    const badgeClass = SKILL_TYPE_COLOR_MAP[skill.color] || 'bg-gray-100 text-gray-600 border-gray-300';

    // Mostrar Dependence / Surface_Distance solo si son distintos de "Common" (valor genérico)
    const showDependence = skill.Dependence && skill.Dependence !== 'Common';
    const showSurfaceDistance = skill.Surface_Distance && skill.Surface_Distance !== 'Common';

    return (
        <div className={`overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-700
            border-l-4 ${borderClass}
            bg-white dark:bg-gray-800
            shadow-sm`}>

            {/* Hero panel con gradiente sutil — interface-design: color carries meaning */}
            <div className="relative px-6 pt-6 pb-5
                bg-gradient-to-br from-transparent
                via-transparent
                to-gray-50/60 dark:to-gray-700/20">

                {/* Fila de icono + texto */}
                <div className="flex gap-5 items-start">
                    <SkillIconHero iconId={skill.icon_id} rarity={skill.rarity} />

                    <div className="flex-1 min-w-0 pt-0.5">
                        {/* Nombre en inglés — Rajdhani: condensada, deportiva, dominio carrera */}
                        <h1 className="text-2xl sm:text-3xl font-bold leading-tight tracking-wide
                            text-gray-900 dark:text-white"
                            style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                            {skill.Skill_name || skill.Skill_name_jp || `Skill #${skill.Id_skill}`}
                        </h1>

                        {/* Nombre JP — metadata secundaria */}
                        {skill.Skill_name && skill.Skill_name_jp && (
                            <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5 tracking-wide">
                                {skill.Skill_name_jp}
                            </p>
                        )}

                        {/* Badge de tipo de skill */}
                        {skill.color && (
                            <span className={`mt-2.5 inline-flex items-center gap-1.5
                                px-2.5 py-1 text-xs font-semibold rounded-full border
                                ${badgeClass} tracking-wide`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                                {skill.color}
                            </span>
                        )}
                    </div>
                </div>

                {/* Descripción — texto primario, máximo legible */}
                {skill.description && (
                    <p className="mt-4 text-[14px] leading-relaxed text-gray-600 dark:text-gray-300
                        pt-4 border-t border-gray-100 dark:border-gray-700/60">
                        {skill.description}
                    </p>
                )}
            </div>

            {/* Stats row — interface-design: separación por contexto, no todo junto */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/60
                flex flex-wrap items-center gap-6">
                <StatPill
                    label="Cost"
                    value={skill.cost !== null ? skill.cost : null}
                    accent="text-blue-600 dark:text-blue-400"
                />
                <StatPill
                    label="Score"
                    value={skill.Score_Value !== null ? parseFloat(skill.Score_Value) : null}
                    accent="text-emerald-600 dark:text-emerald-400"
                />
                {showDependence && (
                    <StatPill
                        label="Dependence"
                        value={skill.Dependence}
                        accent="text-gray-700 dark:text-gray-300"
                    />
                )}
                {showSurfaceDistance && (
                    <StatPill
                        label="Surface / Distance"
                        value={skill.Surface_Distance}
                        accent="text-gray-700 dark:text-gray-300"
                    />
                )}
            </div>

            {/* Evolution chain — solo se renderiza si hay datos */}
            {(baseSkill || upgradeSkill) && (
                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/60
                    flex flex-wrap gap-3">
                    {baseSkill && <EvolutionLink skill={baseSkill} label="Evolved from" />}
                    {upgradeSkill && <EvolutionLink skill={upgradeSkill} label="Evolves into" />}
                </div>
            )}
        </div>
    );
};

export default SkillDetailHeader;
